import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  X,
  Sparkles,
  ShoppingCart,
  Package,
  CreditCard,
  Percent,
  Clock,
  Zap,
  Star,
  Gift,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface FeatureAnnouncementProps {
  isOpen: boolean;
  onClose: () => void;
  onExplore: () => void;
}

const FeatureAnnouncement: React.FC<FeatureAnnouncementProps> = ({
  isOpen,
  onClose,
  onExplore,
}) => {
  const { t } = useTranslation();
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      icon: ShoppingCart,
      title: t("announcement.posSystem"),
      description: t("announcement.posDescription"),
      color: "text-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
    },
    {
      icon: Package,
      title: t("announcement.stockManagement"),
      description: t("announcement.stockDescription"),
      color: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
    },
    {
      icon: Percent,
      title: t("announcement.discountSystem"),
      description: t("announcement.discountDescription"),
      color: "text-purple-500",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
    },
    {
      icon: Clock,
      title: t("announcement.orderHolding"),
      description: t("announcement.orderHoldingDescription"),
      color: "text-orange-500",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
    },
  ];

  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, features.length]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <Card className="relative overflow-hidden border-2 border-gradient-to-r from-blue-500 to-purple-500">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-green-50 dark:from-blue-900/10 dark:via-purple-900/10 dark:to-green-900/10" />
            
            {/* Floating Particles */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-30"
                  animate={{
                    x: [0, 100, 0],
                    y: [0, -50, 0],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 4 + i,
                    repeat: Infinity,
                    delay: i * 0.5,
                  }}
                  style={{
                    left: `${10 + i * 15}%`,
                    top: `${20 + i * 10}%`,
                  }}
                />
              ))}
            </div>

            <CardHeader className="relative text-center pb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="absolute right-2 top-2 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>

              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4"
              >
                <Sparkles className="h-8 w-8 text-white" />
              </motion.div>

              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t("announcement.newFeature")}
              </CardTitle>
              
              <Badge variant="secondary" className="mt-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <Gift className="h-3 w-3 mr-1" />
                {t("announcement.nowAvailable")}
              </Badge>
            </CardHeader>

            <CardContent className="relative space-y-6">
              {/* Main Feature Showcase */}
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  {t("announcement.stockAndPOS")}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {t("announcement.mainDescription")}
                </p>
              </div>

              {/* Animated Feature Cards */}
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  const isActive = index === currentFeature;
                  
                  return (
                    <motion.div
                      key={index}
                      className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                        isActive
                          ? `${feature.bgColor} border-current ${feature.color} shadow-lg scale-105`
                          : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      }`}
                      animate={{
                        scale: isActive ? 1.05 : 1,
                        y: isActive ? -5 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center gap-3">
                        <motion.div
                          animate={{
                            rotate: isActive ? [0, 360] : 0,
                            scale: isActive ? [1, 1.2, 1] : 1,
                          }}
                          transition={{ duration: 0.5 }}
                          className={`p-2 rounded-full ${
                            isActive ? feature.bgColor : "bg-gray-100 dark:bg-gray-700"
                          }`}
                        >
                          <Icon className={`h-5 w-5 ${isActive ? feature.color : "text-gray-500"}`} />
                        </motion.div>
                        <div className="flex-1">
                          <h4 className={`font-semibold text-sm ${isActive ? feature.color : "text-gray-700 dark:text-gray-300"}`}>
                            {feature.title}
                          </h4>
                          <p className={`text-xs ${isActive ? "text-gray-700 dark:text-gray-300" : "text-gray-500"}`}>
                            {feature.description}
                          </p>
                        </div>
                        {isActive && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-green-500"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Key Benefits */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  {t("announcement.keyBenefits")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Zap className="h-3 w-3 text-green-500" />
                    <span>{t("announcement.benefit1")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="h-3 w-3 text-green-500" />
                    <span>{t("announcement.benefit2")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="h-3 w-3 text-green-500" />
                    <span>{t("announcement.benefit3")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="h-3 w-3 text-green-500" />
                    <span>{t("announcement.benefit4")}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={onExplore}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {t("announcement.exploreNow")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={onClose} className="px-6">
                  {t("announcement.later")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FeatureAnnouncement;
